import React, {useCallback, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';


import {ACCOUNT_TABS} from 'src/constants';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {useTabRedirect} from 'src/hooks/useTabRedirect';
import {USERS_TYPES, USER_TYPES_BY_KEYS} from 'src/modules/profile/account/constants';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {getAccountActiveUser} from 'src/openapi/admin/apis/GetAccountActiveUserApi';
import {useAppDispatch} from 'src/store/hooks';

import Icon from '@pb/kit-component-icon';
import {Tab, TabIcon, Tabs} from '@pb/kit-component-tabs-v2';
import {useGateValue} from '@pb/kit-lib-statsig';

import {accountPageTabs} from './account-page-tabs.module.styl';


type Props = {
    accountId: number,
    activeTab: string,
};

export const AccountPageTabs: React.FC<Props> = ({activeTab, accountId}) => {
    const apiDispatch = useAppDispatch();
    const navigate = useNavigate();
    const {hierarchy} = useSelector(selectAccountDetails);
    const isUntracked = hierarchy?.current?.isUntracked ?? true;
    const {accountTabChange} = useEventTracking();

    const {hasAccess, canViewAccountMcp, canViewAccountProducts} = useAdminAccessRight();
    const isPumaGateEnabled = useGateValue(STATSIG_GATES.PUMA);
    const provisioningEnabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const isMcpTabAvailable = canViewAccountMcp(accountId);

    const {users} = useSelector(selectAccountDetails);
    const redirectToSubTab = useTabRedirect(`/profile/account/${accountId}/users-groups/`);

    useEffect(() => {
        if (!users) {
            apiDispatch(getAccountActiveUser({accountId}));
        }
    }, [users, accountId, apiDispatch]);

    const onTabClick = useCallback((tab: string) => {
        if (tab === ACCOUNT_TABS.API.name && isUntracked) {
            return;
        }

        navigate(`/profile/account/${accountId}/${tab}`);
        accountTabChange({previousTab: activeTab, activeTab: tab});
    }, [accountId, activeTab, accountTabChange, isUntracked, navigate]);

    const tabs = useMemo(() => (users && !provisioningEnabled
        ? Object.entries(users).filter(entry => entry[0] !== 'virtual' && entry[1]).map(([tab]) => tab)
        : []
    ), [users, provisioningEnabled]);

    const showAPITab = hasAccess(ACCESS_RIGHTS.API_V2_SCREEN_VIEW);
    const isAccountEntitlementsAllowed = hasAccess(ACCESS_RIGHTS.ACCOUNT_ENTITLEMENTS_VIEW);

    const isMicrositesAllowed = hasAccess(ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW);
    const isConferenceAccount = useConferenceAccountCheck();
    const isConferenceViewAllowed = hasAccess(ACCESS_RIGHTS.CONFERENCE_VIEW);

    return (
        <Tabs onChange={onTabClick} value={activeTab} className={accountPageTabs} style={{width: '100%'}}>
            {[
                <Tab
                    key={ACCOUNT_TABS.PROFILE.name}
                    caption={ACCOUNT_TABS.PROFILE.caption}
                    value={ACCOUNT_TABS.PROFILE.name}
                />,
                <Tab
                    key={ACCOUNT_TABS.DETAILS.name}
                    caption={ACCOUNT_TABS.DETAILS.caption}
                    value={ACCOUNT_TABS.DETAILS.name}
                />,
                <Tab
                    key={ACCOUNT_TABS.USERS.name}
                    caption={ACCOUNT_TABS.USERS.caption}
                    value={ACCOUNT_TABS.USERS.name}
                    endAdornment={<TabIcon position="end"><Icon type="angle-down"/></TabIcon>}
                    menuItems={
                        [
                            {
                                caption: provisioningEnabled ? 'All Users' : 'All users',
                                value: 'all',
                                onClick: () => redirectToSubTab('all'),
                            },
                            ...tabs.map(tab => ({
                                caption: USERS_TYPES[USER_TYPES_BY_KEYS[tab]].caption,
                                value: tab,
                                onClick: () => redirectToSubTab(USERS_TYPES[USER_TYPES_BY_KEYS[tab]].tab),
                            })),
                            (!!users?.virtual || isConferenceAccount) ? {
                                caption: 'Conference users',
                                value: 'virtual',
                                separated: !!tabs.length,
                                onClick: () => redirectToSubTab('virtual'),
                            } : undefined,
                            provisioningEnabled ? {
                                caption: 'Upload List',
                                value: 'upload-list',
                                separated: true,
                                onClick: () => redirectToSubTab('upload-list'),
                            } : undefined,
                            hasAccess(ACCESS_RIGHTS.GROUP_SCREEN_VIEW) ? {
                                caption: 'Groups',
                                value: 'groups',
                                separated: !!tabs.length || !!users?.virtual,
                                onClick: () => redirectToSubTab('groups'),
                            } : undefined,
                        ].filter((item): item is Exclude<typeof item, false | undefined> => Boolean(item))
                    }
                />,
                hasAccess(ACCESS_RIGHTS.LICENSE_SCREEN_VIEW) && (
                    <Tab
                        key={ACCOUNT_TABS.LICENSE.name}
                        caption={ACCOUNT_TABS.LICENSE.caption}
                        value={ACCOUNT_TABS.LICENSE.name}
                    />
                ),
                isPumaGateEnabled && canViewAccountProducts(accountId) && (
                    <Tab
                        key={ACCOUNT_TABS.PRODUCT_ACCESS.name}
                        caption={ACCOUNT_TABS.PRODUCT_ACCESS.caption}
                        value={ACCOUNT_TABS.PRODUCT_ACCESS.name}
                    />
                ),
                isAccountEntitlementsAllowed && (
                    <Tab
                        key={ACCOUNT_TABS.ENTITLEMENTS.name}
                        caption={ACCOUNT_TABS.ENTITLEMENTS.caption}
                        value={ACCOUNT_TABS.ENTITLEMENTS.name}
                    />
                ),
                isConferenceAccount && isConferenceViewAllowed && (
                    <Tab
                        key={ACCOUNT_TABS.CONFERENCE.name}
                        caption={ACCOUNT_TABS.CONFERENCE.caption}
                        value={ACCOUNT_TABS.CONFERENCE.name}
                    />
                ),
                (showAPITab ? (
                    <Tab
                        key={ACCOUNT_TABS.API.name}
                        disabled={isUntracked}
                        caption={ACCOUNT_TABS.API.caption}
                        value={ACCOUNT_TABS.API.name}
                    />
                ) : undefined
                ),
                (isMcpTabAvailable ? (
                    <Tab
                        key={ACCOUNT_TABS.MCP.name}
                        caption={ACCOUNT_TABS.MCP.caption}
                        value={ACCOUNT_TABS.MCP.name}
                    />
                ) : undefined
                ),
                (isMicrositesAllowed ? (
                    <Tab
                        key={ACCOUNT_TABS.MICRO_SITES.name}
                        value={ACCOUNT_TABS.MICRO_SITES.name}
                        caption={ACCOUNT_TABS.MICRO_SITES.caption}
                    />
                ) : undefined
                ),
                <Tab
                    key={ACCOUNT_TABS.SETTINGS.name}
                    caption={ACCOUNT_TABS.SETTINGS.caption}
                    value={ACCOUNT_TABS.SETTINGS.name}
                />,
                <Tab
                    key={ACCOUNT_TABS.AUDIT_LOG.name}
                    caption={ACCOUNT_TABS.AUDIT_LOG.caption}
                    value={ACCOUNT_TABS.AUDIT_LOG.name}
                />,
            ].filter(e => e)}
        </Tabs>
    );
};
